import React from 'react';
import styled from "styled-components";
import {CustomCard} from "../../../../../components/UI/AntCustom/CustomCard";
import {Loader} from "../../../../../components/UI/Loader/Loader";
import {useIntl, defineMessages} from "react-intl";

const UsageStatsMessages = defineMessages({
  title: {
    id: "usageStats.title",
    defaultMessage: "Current Usage",
  },
  users: {
    id: "usageStats.users",
    defaultMessage: "Active Users",
  },
  aircraft: {
    id: "usageStats.aircraft",
    defaultMessage: "Active Aircraft",
  },
  seatsUsed: {
    id: "usageStats.seatsUsed",
    defaultMessage: "{used} of {subscribed} seats used",
  },
  aircraftUsed: {
    id: "usageStats.aircraftUsed",
    defaultMessage: "{used} of {subscribed} aircraft slots used",
  },
  billedPerUnit: {
    id: "usageStats.billedPerUnit",
    defaultMessage: "Billed per user/aircraft",
  },
  billedPerUser: {
    id: "usageStats.billedPerUser",
    defaultMessage: "Billed per user",
  },
  estimatedMonthly: {
    id: "usageStats.estimatedMonthly",
    defaultMessage: "Estimated monthly",
  },
  estimatedMonthlyForFirstMonths: {
    id: "usageStats.estimatedMonthlyForFirstMonths",
    defaultMessage: "For the first {months} months",
  },
  thenAmountPerMonth: {
    id: "usageStats.thenAmountPerMonth",
    defaultMessage: "Then {amount}/month",
  },
  thenAmountPerMonthAfterDate: {
    id: "usageStats.thenAmountPerMonthAfterDate",
    defaultMessage: "Then {amount}/month after {date}",
  },
});

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

const StatHeader = styled.div`
  font-size: 14px;
  color: #666666;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #000000;
`;

const StatHint = styled.div`
  font-size: 12px;
  color: #999999;
  margin-top: 4px;
`;

function formatMoney(cents: number, currency: string): string {
  const code = currency?.toUpperCase() || 'USD';
  if (code === 'USD') {
    return `$${(cents / 100).toFixed(2)}`;
  }
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(cents / 100);
}

interface IUsageStats {
  activeUsers?: number;
  activeAircraft?: number;
  subscribedUsersCount?: number;
  subscribedAircraftCount?: number;
  isOperator: boolean;
  pricePerSeatCents?: number;
  pricePerAircraftCents?: number;
  currency?: string;
  discountPercent?: number;
  discountDurationMonths?: number;
  discountEndsAt?: string; // ISO date when discount period ends
  discountApplies?: boolean; // true when user qualifies for discount (first-time subscription)
  isLoading?: boolean;
}

export const UsageStats: React.FC<IUsageStats> = ({
  activeUsers,
  activeAircraft,
  subscribedUsersCount,
  subscribedAircraftCount,
  isOperator,
  pricePerSeatCents,
  pricePerAircraftCents,
  currency = 'usd',
  discountPercent,
  discountDurationMonths,
  discountEndsAt,
  discountApplies = false,
  isLoading = false,
}) => {
  const intl = useIntl();
  const users = activeUsers ?? 0;
  const aircraft = activeAircraft ?? 0;
  const hasSubscribedUsers = subscribedUsersCount != null && subscribedUsersCount > 0;
  const hasSubscribedAircraft = subscribedAircraftCount != null && subscribedAircraftCount > 0;
  const seatCents = pricePerSeatCents ?? 0;
  const aircraftCents = pricePerAircraftCents ?? 0;
  const hasPrices = seatCents > 0 || aircraftCents > 0;

  // Estimated monthly: what they pay (subscribed quantities). Fall back to billed when no subscription.
  const usersForPrice = subscribedUsersCount ?? users;
  const aircraftForPrice = isOperator ? (subscribedAircraftCount ?? aircraft) : 0;
  const fullCents = usersForPrice * seatCents + aircraftForPrice * aircraftCents;
  const totalCents = discountApplies && discountPercent != null && discountPercent > 0
    ? Math.round(fullCents * (100 - discountPercent) / 100)
    : fullCents;

  if (isLoading) {
    return (
      <CustomCard width={"max"} style={{ width: '100%' }}>
        <div style={{fontSize: '16px', fontWeight: 600, marginBottom: '8px'}}>
          {intl.formatMessage(UsageStatsMessages.title)}
        </div>
        <Loader />
      </CustomCard>
    );
  }

  return (
    <CustomCard width={"max"} style={{ width: '100%' }}>
      <div style={{fontSize: '16px', fontWeight: 600, marginBottom: '8px'}}>
        {intl.formatMessage(UsageStatsMessages.title)}
      </div>

      <StatItem>
        <StatHeader>{intl.formatMessage(UsageStatsMessages.users)}</StatHeader>
        <StatValue>
          {hasSubscribedUsers
            ? intl.formatMessage(UsageStatsMessages.seatsUsed, { used: users, subscribed: subscribedUsersCount })
            : users}
        </StatValue>
      </StatItem>

      {isOperator && (
        <StatItem>
          <StatHeader>{intl.formatMessage(UsageStatsMessages.aircraft)}</StatHeader>
          <StatValue>
            {hasSubscribedAircraft
              ? intl.formatMessage(UsageStatsMessages.aircraftUsed, { used: aircraft, subscribed: subscribedAircraftCount })
              : aircraft}
          </StatValue>
        </StatItem>
      )}

      <StatHint>
        {isOperator
          ? intl.formatMessage(UsageStatsMessages.billedPerUnit)
          : intl.formatMessage(UsageStatsMessages.billedPerUser)}
      </StatHint>

      {hasPrices && (
        <StatItem>
          <StatHeader>{intl.formatMessage(UsageStatsMessages.estimatedMonthly)}</StatHeader>
          <StatValue>{formatMoney(totalCents, currency)}</StatValue>
          {discountApplies && discountDurationMonths != null && discountDurationMonths > 0 && (
            <StatHint>
              {intl.formatMessage(UsageStatsMessages.estimatedMonthlyForFirstMonths, {
                months: discountDurationMonths,
              })}
            </StatHint>
          )}
          {discountApplies && fullCents > 0 && (
            <StatHint>
              {discountEndsAt
                ? intl.formatMessage(UsageStatsMessages.thenAmountPerMonthAfterDate, {
                    amount: formatMoney(fullCents, currency),
                    date: intl.formatDate(discountEndsAt, { month: 'short', year: 'numeric' }),
                  })
                : intl.formatMessage(UsageStatsMessages.thenAmountPerMonth, {
                    amount: formatMoney(fullCents, currency),
                  })}
            </StatHint>
          )}
        </StatItem>
      )}
    </CustomCard>
  );
};
